// packages/utils/src/shared/phones/sanitizePhoneNumberInput.ts
//
// The one rule for what may be typed into a phone NUMBER field, shared by the
// web `PhoneInput` (packages/ui) and the native `PhoneNumberField`
// (apps/mobile-sales). The country half is always picked from a list, so the
// number half must hold nothing but the subscriber digits: `buildPhoneLink`
// composes `+<callingCode><number>`, and any separator, letter or trunk zero
// that survives here ends up inside a tel: / wa.me link that silently fails to
// dial.
//
// Leading zeros go too — they are the national trunk prefix (`07…` in SE/GB,
// `09…` in AR), which is exactly the digit an international calling code
// replaces. Keeping it would produce `+4607…`.

/** Digits only, with any leading national trunk zeros stripped. */
export function sanitizePhoneNumberInput(raw: string): string {
  return raw.replace(/\D/g, '').replace(/^0+/, '');
}

/**
 * Calling codes whose numbers keep their leading 0 when dialled from abroad —
 * there it is part of the number, not a national trunk prefix: Italian, San
 * Marino and Vatican landlines (`+39 06 …`), and Côte d'Ivoire's ten-digit
 * numbers (since 2021).
 */
const LEADING_ZERO_IN_NUMBER_CALLING_CODES: ReadonlySet<number> = new Set([39, 378, 379, 225]);

/**
 * The subscriber digits of a number in a KNOWN country (its calling code):
 * `sanitizePhoneNumberInput`, except that a country whose numbers begin with
 * a 0 keeps it — stripping it there stores a different number.
 */
export function sanitizeSubscriberNumber(raw: string, callingCode: number): string {
  const digits = raw.replace(/\D/g, '');
  return LEADING_ZERO_IN_NUMBER_CALLING_CODES.has(callingCode) ? digits : digits.replace(/^0+/, '');
}
